import { Entity, Vec3, type Application } from 'playcanvas';
import { AvatarController } from '../player/AvatarController';
import { avatarConfig, type AvatarConfig } from '../config/avatar.config';
import { getLayers, worldIds } from '../render/LayerManager';
import { SnapshotBuffer } from './SnapshotBuffer';
import type { Collision } from '../collision';
import { levelTitle, type Avatar, type Player, type Profile, type Snapshot, type StateBatch, type ActionState, type PlayerState } from '../../../../vrroom_typescript/src/platform/contracts';

export const modelConfig = (avatar: Avatar): AvatarConfig => ({...avatarConfig, ...avatar.config, avatarUrl: avatar.modelUrl});
interface Remote { player: Player; root: Entity; model: AvatarController; buffer: SnapshotBuffer; label: HTMLDivElement; nameSpan: HTMLSpanElement; micIcon: SVGSVGElement; micOn: boolean | null; animationKey: string; feetDrop: number; closeHidden: boolean; }

/** Approximate body capsule (feet→head) used for nameplate occlusion tests. */
const OCCLUDER_HEIGHT = 1.8;
const OCCLUDER_RADIUS = 0.35;
/** A hit only hides the nameplate when it is at least this much closer than the plate itself. */
const NAMEPLATE_OCCLUDE_MARGIN = 0.2;
/** 头牌锚点在（脚底+身体胶囊高度）之上再抬高的余量：标签悬在头顶上方一点。 */
const NAMEPLATE_LIFT = 0.25;
/** 远程玩家模型（眼位）离相机小于此距离时整体隐藏：近裁剪面会切进模型内部、
 *  露出身体内壁。带迟滞恢复，避免在阈值附近闪烁。 */
const REMOTE_HIDE_DISTANCE = 0.6;
const REMOTE_SHOW_DISTANCE = 0.85;

/** Ray (origin, normalized dir) vs capsule (pa→pb, radius). Returns distance t, or -1. */
function rayCapsule(ro: Vec3, rd: Vec3, pa: Vec3, pb: Vec3, radius: number): number {
    const bax = pb.x - pa.x, bay = pb.y - pa.y, baz = pb.z - pa.z;
    const oax = ro.x - pa.x, oay = ro.y - pa.y, oaz = ro.z - pa.z;
    const baba = bax * bax + bay * bay + baz * baz;
    const bard = bax * rd.x + bay * rd.y + baz * rd.z;
    const baoa = bax * oax + bay * oay + baz * oaz;
    const rdoa = rd.x * oax + rd.y * oay + rd.z * oaz;
    const oaoa = oax * oax + oay * oay + oaz * oaz;
    const a = baba - bard * bard;
    const b = baba * rdoa - baoa * bard;
    const c = baba * oaoa - baoa * baoa - radius * radius * baba;
    const h = b * b - a * c;
    if (h >= 0) {
        const sh = Math.sqrt(h);
        if (Math.abs(a) > 1e-9) {
            const t = (-b - sh) / a;
            const y = baoa + t * bard;
            // Cylindrical body between the two sphere centers.
            if (y > 0 && y < baba) return t;
        }
        // Spherical caps: pick the end sphere the ray points at.
        const ox = baoa > baba ? ro.x - pb.x : oax;
        const oy = baoa > baba ? ro.y - pb.y : oay;
        const oz = baoa > baba ? ro.z - pb.z : oaz;
        const bb = rd.x * ox + rd.y * oy + rd.z * oz;
        const cc = ox * ox + oy * oy + oz * oz - radius * radius;
        const hh = bb * bb - cc;
        if (hh >= 0) return -bb - Math.sqrt(hh);
    }
    return -1;
}

/** 名牌旁的开麦小图标（玉青色与底部麦克风按钮开麦态一致）；默认隐藏，开麦才显示。 */
function buildMicIcon(): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '14');
    svg.setAttribute('height', '14');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', '#8ef0c6');
    svg.setAttribute('stroke-width', '2.2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.style.display = 'none';
    svg.style.flex = '0 0 auto';
    const capsule = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    capsule.setAttribute('x', '9'); capsule.setAttribute('y', '3');
    capsule.setAttribute('width', '6'); capsule.setAttribute('height', '11');
    capsule.setAttribute('rx', '3');
    const arc = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    arc.setAttribute('d', 'M5 11a7 7 0 0 0 14 0');
    const stem = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    stem.setAttribute('d', 'M12 18v3');
    svg.append(capsule, arc, stem);
    return svg;
}

/** Each remote has its own animation state; all players using a GLB share its assets. */
export class RemotePlayers {
    private players = new Map<number, Remote>();
    private roomId = '';
    private memberRevision = -1;
    private layer = document.createElement('div');
    private panel = document.createElement('div');
    private panelUserId: number | null = null;
    private scratchDir = new Vec3();
    private scratchBase = new Vec3();
    private scratchTop = new Vec3();
    constructor(private app: Application, private camera: Entity, private localId: number,
        private localPlayer?: () => { position: Vec3; feetDrop: number } | null,
        /** 当前场景碰撞体：头牌按相机→头牌线段查询场景遮挡（与视频面板的
         *  遮挡代理同源几何——墙体/建筑既挡面板也挡头牌）。 */
        private sceneCollision?: () => Collision | null) {
        // 名牌层与成员信息面板挂进 #app-root（z=5/6：canvas(1) 之上、#ui-layer(15)
        // 之下），与设置弹窗同处一个层叠上下文，任何模式下弹窗都能盖住名牌。
        // 不能挂 body：mobile-virtual-landscape 给 #app-root 加 transform 旋转
        // 会生成独立层叠上下文，挂在 body 的 z12 名牌层反而浮在所有 UI 弹窗
        // 之上，且名牌坐标（canvas 坐标系）不随旋转映射、位置也是错的。
        const appRoot = document.getElementById('app-root') ?? document.body;
        this.layer.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:5;overflow:hidden';
        appRoot.append(this.layer);
        this.panel.style.cssText = 'position:fixed;right:16px;top:50%;transform:translateY(-50%);z-index:6;min-width:220px;padding:14px 16px;border:1px solid #ffffff50;border-radius:12px;background:#14222ce8;color:white;font:14px system-ui;box-shadow:0 8px 24px #0008';
        this.panel.hidden = true;
        appRoot.append(this.panel);
    }
    clear() { for (const id of this.players.keys()) this.remove(id); this.roomId = ''; this.memberRevision = -1; }
    private remove(id: number) { const r = this.players.get(id); if (!r) return; r.model.destroy(); r.root.destroy(); r.label.remove(); this.players.delete(id); if (this.panelUserId === id) this.hidePanel(); }
    private showPanel(profile: Profile) {
        this.panelUserId = profile.id;
        const close = document.createElement('button');
        close.textContent = '×';
        close.style.cssText = 'position:absolute;top:6px;right:8px;border:none;background:none;color:#ffffffaa;font:18px system-ui;cursor:pointer;padding:2px 6px';
        close.addEventListener('click', () => this.hidePanel());
        const title = document.createElement('div');
        title.style.cssText = 'margin:2px 0 10px;font-weight:600;letter-spacing:1px;color:#ffffffcc;font-size:12px';
        title.textContent = '成员信息';
        const row = (name: string, value: string) => {
            const d = document.createElement('div');
            d.style.cssText = 'display:flex;justify-content:space-between;gap:18px;padding:6px 0;border-top:1px solid #ffffff1c';
            const k = document.createElement('span'); k.style.color = '#ffffff99'; k.textContent = name;
            const v = document.createElement('strong'); v.style.fontWeight = '600'; v.textContent = value;
            d.append(k, v); return d;
        };
        this.panel.replaceChildren(close, title,
            row('昵称', profile.username),
            row('级别', `${levelTitle(profile.level)} · ${profile.level}级`),
            row('单位', profile.organization || '—'));
        this.panel.hidden = false;
    }
    private hidePanel() { this.panel.hidden = true; this.panelUserId = null; }
    private upsert(player: Player) {
        const id = player.profile.id; if (id === this.localId) return;
        let r = this.players.get(id);
        if (r && player.state.membershipEpoch < r.player.state.membershipEpoch) return;
        if (r && (r.player.avatar.id !== player.avatar.id || r.player.avatar.revision !== player.avatar.revision || r.player.state.membershipEpoch !== player.state.membershipEpoch)) { this.remove(id); r = undefined; }
        if (!r) {
            const root = new Entity(`remote-${id}`), avatarRoot = new Entity('avatar'); root.addChild(avatarRoot); this.app.root.addChild(root);
            const cfg = modelConfig(player.avatar);
            const model = new AvatarController(this.app, {avatarRoot}, cfg, worldIds());
            // Real-mesh depth occluder: depth-only clones of the avatar's meshes
            // are drawn on the AvatarDepth layer (which clears the scene depth
            // first), so the local third-person avatar is occluded by this
            // player's true animated silhouette instead of a proxy capsule.
            const layers = getLayers();
            if (layers) model.enableDepthOccluder(layers.avatarDepth);
            const label = document.createElement('div');
            label.style.cssText = 'position:absolute;left:0;top:0;display:flex;align-items:center;justify-content:center;gap:6px;padding:5px 10px;border:1px solid #ffffff50;border-radius:8px;background:#14222cdd;color:white;font:13px system-ui;white-space:nowrap;box-shadow:0 2px 8px #0005;pointer-events:auto;cursor:pointer';
            label.title = '点击查看成员信息';
            label.addEventListener('click', () => {
                const current = this.players.get(id); if (!current) return;
                if (this.panelUserId === id) this.hidePanel(); else this.showPanel(current.player.profile);
            });
            const nameSpan = document.createElement('span');
            // 开麦指示：名牌旁的小麦克风（随 player:state 20Hz 同步，开麦才显示）。
            const micIcon = buildMicIcon();
            label.append(nameSpan, micIcon);
            this.layer.append(label);
            r = {player: structuredClone(player), root, model, buffer: new SnapshotBuffer(), label, nameSpan, micIcon, micOn: null, animationKey: '', feetDrop: cfg.eyeHeight + cfg.groundOffset, closeHidden: false};
            this.players.set(id, r); void model.load();
        }
        r.buffer.push(player.state);
        if (player.profile.revision >= r.player.profile.revision) { r.player.profile = structuredClone(player.profile); r.player.avatar = structuredClone(player.avatar); }
        if (player.action.actionSeq >= r.player.action.actionSeq) r.player.action = structuredClone(player.action);
        if (player.state.seq >= r.player.state.seq) r.player.state = structuredClone(player.state);
        r.player.status = player.status;
        r.nameSpan.textContent = r.player.profile.username;
        if (this.panelUserId === id) this.showPanel(r.player.profile);
    }
    snapshot(snapshot: Snapshot) {
        if (snapshot.room.id !== this.roomId) { this.clear(); this.roomId = snapshot.room.id; }
        if (snapshot.memberRevision < this.memberRevision) return;
        this.memberRevision = snapshot.memberRevision;
        const ids = new Set(snapshot.players.map(p => p.profile.id));
        for (const id of this.players.keys()) if (!ids.has(id)) this.remove(id);
        for (const player of snapshot.players) this.upsert(player);
    }
    states(batch: StateBatch) {
        for (const state of batch.states) {
            const r = this.players.get(state.userId);
            if (!r || state.membershipEpoch !== r.player.state.membershipEpoch) continue;
            r.buffer.push(state);
            if (state.poseRevision > r.player.state.poseRevision || state.seq >= r.player.state.seq) r.player.state = structuredClone(state);
        }
    }
    event(name: string, data: any) {
        if (name === 'player:joined') { if (data.memberRevision < this.memberRevision) return; this.memberRevision = data.memberRevision; this.upsert(data.player); return; }
        const r = this.players.get(data.userId);
        if (!r || data.membershipEpoch !== r.player.state.membershipEpoch) return;
        if (name === 'player:left' && data.memberRevision >= this.memberRevision) { this.memberRevision = data.memberRevision; this.remove(data.userId); }
        if (name === 'player:action' && data.action.actionSeq >= r.player.action.actionSeq) r.player.action = data.action;
        if (name === 'player:profile' && data.profile.revision >= r.player.profile.revision) this.upsert({...r.player, profile: data.profile, avatar: data.avatar});
    }
    update(serverTime: number) {
        const time = serverTime - 100, point = new Vec3(), screen = new Vec3(), delta = new Vec3(), body = new Vec3();
        const camPos = this.camera.getPosition();
        for (const r of this.players.values()) {
            const state = r.buffer.sample(time); if (!state) continue;
            // 隐身模式：网格级隐藏（模型 + 深度遮挡克隆），名牌也隐藏。
            // 不切换实体 enabled —— 禁用 render 组件会销毁其缓存的
            // skinInstance（onDisable → _clearSkinInstances），而深度遮挡
            // 克隆共享它，恢复后会把已销毁实例带进渲染导致崩溃。
            // 保留 Remote 条目与状态插值，取消隐身后可立即平滑恢复。
            if (r.player.profile.hidden) { r.model.setVisible(false); r.label.hidden = true; continue; }
            r.root.setPosition(...state.position);
            // 贴脸隐藏（带迟滞）：远程玩家离相机过近时，近裁剪面会切进模型内部、
            // 能看到身体内壁，直接隐藏其模型与深度遮挡，拉开距离后恢复。
            body.set(state.position[0], state.position[1], state.position[2]);
            const bodyDistance = body.sub(camPos).length();
            if (bodyDistance <= REMOTE_HIDE_DISTANCE) r.closeHidden = true;
            else if (bodyDistance >= REMOTE_SHOW_DISTANCE) r.closeHidden = false;
            r.model.setVisible(!r.closeHidden);
            this.animate(r, state, r.player.action, time);
            r.model.lockRootMotion(r.player.action.actionId ? 'action' : 'locomotion', r.player.action.actionId);
            // The non-animated child carries the exact interpolated body orientation.
            (r.root.children[0] as Entity).setLocalRotation(...state.rotation);
            // 头牌锚点：眼部位置换算回脚底，抬高一个身体胶囊高度再加少量余量
            // （标签底边悬在头顶上方一点，随各形象身高自适应）。
            point.set(state.position[0], state.position[1] - r.feetDrop + OCCLUDER_HEIGHT + NAMEPLATE_LIFT, state.position[2]);
            // 开麦图标随插值状态同步（micOn 缓存避免每帧动 DOM）。
            const mic = state.mic === true;
            if (mic !== r.micOn) { r.micOn = mic; r.micIcon.style.display = mic ? '' : 'none'; }
            delta.sub2(point, camPos);
            const distance = delta.length();
            const visible = distance < 60 && delta.dot(this.camera.forward) > 0 && this.camera.camera
                && !this.nameplateOccluded(point, distance, r);
            r.label.hidden = !visible;
            if (visible) {
                this.camera.camera!.worldToScreen(point, screen);
                r.label.style.transform = `translate(${screen.x}px,${screen.y}px) translate(-50%,-100%) scale(${Math.max(0.65, Math.min(1, 12 / Math.max(1, distance)))})`;
            }
        }
        return this.players.size > 0;
    }

    /** True when the segment camera→nameplate passes through scene geometry or
     *  a player capsule (local or remote) before reaching the plate — the DOM
     *  label is then hidden so walls/bodies occlude nameplates.
     *  owner（头牌主人）自己的胶囊不参与检测：锚点贴在头顶、即胶囊顶端
     *  球心处（在胶囊顶面下方 0.35m），射线命中胶囊壁距锚点 0.35m、超出
     *  0.2m 容差，不排除会永远自遮挡导致头牌不可见。其他人的身体遮挡仍生效。 */
    private nameplateOccluded(point: Vec3, distance: number, owner: Remote): boolean {
        const camPos = this.camera.getPosition();
        this.scratchDir.sub2(point, camPos).divScalar(Math.max(distance, 1e-6));
        const limit = distance - NAMEPLATE_OCCLUDE_MARGIN;
        // 场景遮挡：线段先撞到墙/建筑就隐藏头牌。碰撞体未就绪（手机端延迟
        // 加载/无碰撞场景）时跳过，只保留胶囊遮挡——与视频面板「遮挡体到位
        // 前不遮挡」的行为一致。BVH 射线查询按头牌数量每帧几次，开销可忽略。
        const scene = this.sceneCollision?.();
        if (scene) {
            const hit = scene.queryRay(camPos.x, camPos.y, camPos.z, this.scratchDir.x, this.scratchDir.y, this.scratchDir.z, limit);
            if (hit) {
                const dx = hit.x - camPos.x, dy = hit.y - camPos.y, dz = hit.z - camPos.z;
                if (dx * dx + dy * dy + dz * dz < limit * limit) return true;
            }
        }
        const local = this.localPlayer?.();
        if (local && this.segmentHitsCapsule(camPos, this.scratchDir, limit, local.position, local.feetDrop)) return true;
        for (const other of this.players.values()) {
            if (other === owner) continue;
            // 隐身玩家的胶囊不遮挡别人：他自己都不可见，不应影响他人名牌。
            if (other.player.profile.hidden) continue;
            const feet = other.root.getPosition();
            if (this.segmentHitsCapsule(camPos, this.scratchDir, limit, feet, other.feetDrop)) return true;
        }
        return false;
    }

    private segmentHitsCapsule(origin: Vec3, dir: Vec3, limit: number, feet: Vec3, feetDrop: number): boolean {
        this.scratchBase.set(feet.x, feet.y - feetDrop, feet.z);
        this.scratchTop.set(this.scratchBase.x, this.scratchBase.y + OCCLUDER_HEIGHT, this.scratchBase.z);
        const t = rayCapsule(origin, dir, this.scratchBase, this.scratchTop, OCCLUDER_RADIUS);
        return t >= 0 && t < limit;
    }
    private animate(r: Remote, state: PlayerState, action: ActionState, time: number) {
        const layer = r.model.animLayer; if (!layer) return;
        const acting = action.actionId && time >= action.startedAt && (action.loop || time < action.startedAt + action.duration);
        let name = acting ? action.actionId! : state.locomotion === 'fly' ? 'idle' : state.locomotion;
        if (!r.model.hasState(name)) name = 'idle';
        const key = `${state.membershipEpoch}:${state.poseRevision}:${name}:${acting ? action.actionSeq : state.motionRevision}`;
        if (key === r.animationKey) return;
        layer.transition(name, name === 'jump' ? 0.08 : 0.15);
        const elapsed = Math.max(0, time - (acting ? action.startedAt : state.motionStartedAt)) / 1000;
        const duration = layer.activeStateDuration ?? 0;
        if (duration > 0) layer.activeStateCurrentTime = acting && !action.loop || name === 'jump' ? Math.min(duration * 0.999, elapsed) : elapsed % duration;
        r.animationKey = key;
    }
    destroy() { this.clear(); this.layer.remove(); this.panel.remove(); }
}

